import { Box, Grid2, Typography } from "@mui/material";
import { DataGrid, Toast } from "../common";
import {
  FetchPaymentRecordsRequestParams,
  ToastOptions,
  PaymentsInfo,
  PaymentRecordsFilters,
  PaymentRecord,
} from "../../types";
import {
  downloadFile,
  formatCurrency,
  getPaymentStatus,
  theme,
} from "../../utils";
import { useAtom } from "jotai";
import {
  paymentsDataGridPaginationAtom,
  paymentsFilterStateAtom,
  selectedAccountAtom,
  selectedAccountIdAtom,
  selectedOrgIdAtom,
  userAtom,
} from "../../atoms";
import { GridColDef, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { formatDate, getDateRange } from "../../utils/dateUtil";
import {
  DateRangeOptions,
  PaymentStatus,
  Role,
  TransactionType,
} from "../../enums";
import { PaymentsActionIcons, PaymentsToolBar } from "./fragments";
import { useSearchParams } from "react-router-dom";
import {
  useCreditCardReturnMutation,
  useCreditCardReversalMutation,
  useFetchDownloadPaymentsCsvQuery,
  useFetchPaymentRecordsQuery,
} from "../../api";
import { PAYMENT_TRANSACTION_TILES } from "../../constants";
import { queryClient } from "../../App";

export const Payments = () => {
  const [user] = useAtom(userAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [selectedAccount] = useAtom(selectedAccountAtom);
  const [selectedAccountId] = useAtom(selectedAccountIdAtom);
  const [filterValues, setFilterValues] = useAtom(paymentsFilterStateAtom);
  const [paymentRecords, setPaymentRecords] = useState<PaymentsInfo>();
  const [queryParams, setQueryParams] = useSearchParams();
  const [paymentsPaginationModel, setPaymentsPaginationModel] = useAtom(
    paymentsDataGridPaginationAtom,
  );

  const [paymentRecordsFilters, setPaymentRecordsFilters] =
    useState<PaymentRecordsFilters>({
      fromDate: getDateRange("last12Months").startDate,
      toDate: getDateRange("last12Months").endDate,
      sortBy: "transactionDate",
      sortOrder: "desc",
      paymentStatus: PaymentStatus.All,
      transactionType: TransactionType.All,
      search: "",
    });
  const [toastOptions, setToastOptions] = useState<ToastOptions>({
    open: false,
    message: "",
    severity: "info",
  });
  const [selectedDateRange, setSelectedDateRange] =
    useState<string>("last12Months");

  const [showMobileTransactionMenu, setShowMobileTransactionMenu] =
    useState(true);

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const sortField = sortModel[0].field;
      const sortOrder: GridSortDirection = sortModel[0].sort;
      if (sortOrder) {
        setPaymentRecordsFilters({
          ...paymentRecordsFilters,
          sortBy: sortField,
          sortOrder: sortOrder,
        });
      }
    } else if (paymentRecordsFilters.sortBy !== "") {
      setPaymentRecordsFilters({
        ...paymentRecordsFilters,
        sortBy: "",
        sortOrder: "",
      });
    }
  };
  const buildPaymentsRequestParams = (): FetchPaymentRecordsRequestParams => {
    return {
      orgId: selectedOrgId,
      accountId:
        user?.roleCode === Role.SuperAdmin || user?.roleCode === Role.Admin
          ? paymentRecordsFilters?.accountId
            ? paymentRecordsFilters?.accountId
            : selectedAccountId || 0
          : selectedAccountId || 0,
      paymentStatus: paymentRecordsFilters?.paymentStatus || PaymentStatus.All,
      transactionType:
        paymentRecordsFilters?.transactionType || TransactionType.All,
      invoiceNumber: paymentRecordsFilters.invoiceNumber || "0",
      filters: {
        page: paymentRecordsFilters?.page ? paymentRecordsFilters?.page + 1 : 1,
        pageSize: paymentRecordsFilters?.pageSize,
        fromDate: paymentRecordsFilters?.fromDate,
        toDate: paymentRecordsFilters?.toDate,
        sortBy: paymentRecordsFilters?.sortBy,
        sortOrder: paymentRecordsFilters?.sortOrder,
        search: paymentRecordsFilters?.search,
      },
    };
  };

  const paymentRequestParams = buildPaymentsRequestParams();

  const isAllowedToFetch =
    selectedOrgId &&
    !(user?.roleCode === Role.Customer && paymentRequestParams.accountId === 0);

  const downloadPaymentsQuery = useFetchDownloadPaymentsCsvQuery(
    paymentRequestParams,
    {
      enabled: false,
    },
  );

  const fetchPaymentRecordsQuery = useFetchPaymentRecordsQuery(
    paymentRequestParams,
    {
      enabled: isAllowedToFetch ? true : false,
    },
  );

  const creditCardReturnMutation = useCreditCardReturnMutation();
  const creditCardReversalMutation = useCreditCardReversalMutation();

  const columns: GridColDef[] = [
    {
      field: "transactionDate",
      headerName: "Date",
      width: 210,
      editable: false,
      valueFormatter: (value) => {
        return formatDate(value, "dddd - MMMM DD,YYYY");
      },
    },
    {
      field: "accountName",
      headerName: "Account Name",
      minWidth: 200,
      editable: false,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      width: 140,
      editable: false,
    },
    {
      field: "paymentMethod",
      headerName: "Payment Method",
      width: 130,
      headerAlign: "center",
      align: "center",
      editable: false,
      valueFormatter: (value) => {
        const paymentMethod = value as string;

        const paymentMethodLabels: Record<string, string> = {
          ach: "ACH",
          card: "Card",
          check: "Check",
          cash: "Cash",
        };

        return (
          paymentMethodLabels[paymentMethod.toLowerCase()] || paymentMethod
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: false,
      valueFormatter: (value, row) => {
        const isRefund = row.transactionType === TransactionType.Refund;
        return `${isRefund ? "-" : ""} ${formatCurrency(value)}`;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 170,
      headerAlign: "center",
      align: "center",
      editable: false,
      valueGetter: (value) => getPaymentStatus(value),
    },
    {
      field: "transactionId",
      headerName: "Transaction Id",
      width: 150,
      headerAlign: "center",
      align: "center",
      editable: false,
    },
  ];

  if (user?.roleCode === Role.SuperAdmin || user?.roleCode === Role.Admin) {
    columns.push({
      field: "Actions",
      headerName: "Actions",
      width: 300,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          (params.row.transactionType === TransactionType.Sale ||
            params.row.transactionType === TransactionType.Refund) &&
          (params.row.status === PaymentStatus.PaymentSuccess ||
            params.row.status === PaymentStatus.ReturnSuccess ||
            params.row.status === PaymentStatus.TransactionInitiated) && (
            <PaymentsActionIcons
              paymentRecord={params.row}
              handleRefund={handleRefund}
              handleReverse={handleReverse}
            />
          )
        );
      },
    });
  }

  const handleRefresh = () => {
    isAllowedToFetch && fetchPaymentRecordsQuery.refetch();
  };
  const handleDownload = () => {
    isAllowedToFetch && downloadPaymentsQuery.refetch();
  };

  const handleRefund = (
    data: Pick<
      PaymentRecord,
      "id" | "orgId" | "accountId" | "invoiceNumber"
    > & { refundAmount: any },
  ) => {
    creditCardReturnMutation.mutate({
      orgId: data.orgId,
      accountId: data.accountId,
      invoiceNumber: data.invoiceNumber,
      paymentRecordId: data.id,
      amount: Number(data.refundAmount),
    });
  };

  const handleReverse = (payment: PaymentRecord) => {
    creditCardReversalMutation.mutate({
      orgId: payment.orgId,
      accountId: payment.accountId,
      invoiceNumber: payment.invoiceNumber,
      paymentRecordId: payment.id,
    });
  };

  const resetPage = () => {
    setPaymentsPaginationModel({
      ...paymentsPaginationModel,
      page: 0,
    });
  };

  useEffect(() => {
    if (fetchPaymentRecordsQuery.isSuccess) {
      setPaymentRecords(fetchPaymentRecordsQuery.data);
    }
  }, [fetchPaymentRecordsQuery.isSuccess, fetchPaymentRecordsQuery.data]);

  useEffect(() => {
    if (downloadPaymentsQuery.isSuccess) {
      const accountName = selectedAccount ? selectedAccount.accountName : "";
      const fileName =
        [
          accountName,
          paymentRecordsFilters.fromDate || "",
          paymentRecordsFilters.fromDate && paymentRecordsFilters.toDate
            ? "to"
            : "",
          paymentRecordsFilters.toDate || "",
        ]
          .filter(Boolean)
          .join("_") + "_payments.csv";
      const blob = new Blob([downloadPaymentsQuery.data], {
        type: "text/csv",
      });
      const linkToCsv = URL.createObjectURL(blob);

      if (linkToCsv) downloadFile(linkToCsv, fileName);

      queryClient.resetQueries("downloadPayments");
    }
  }, [
    downloadPaymentsQuery.isSuccess,
    downloadPaymentsQuery.data,
    downloadPaymentsQuery.error,
    downloadPaymentsQuery.isError,
    downloadPaymentsQuery.isRefetching,
  ]);

  useEffect(() => {
    setPaymentRecordsFilters({
      ...paymentRecordsFilters,
      page: paymentsPaginationModel.page,
      pageSize: paymentsPaginationModel.pageSize,
    });
  }, [paymentsPaginationModel]);

  useEffect(() => {
    const dateRange = queryParams.get("dateRange");
    const dateFilters: Partial<PaymentRecordsFilters> = {
      fromDate: queryParams.get("fromDate") || undefined,
      toDate: queryParams.get("toDate") || undefined,
    };
    if (
      dateRange &&
      Object.values(DateRangeOptions).includes(dateRange as DateRangeOptions)
    ) {
      if (dateRange !== DateRangeOptions.CustomDate) {
        dateFilters.fromDate = getDateRange(dateRange).startDate;
        dateFilters.toDate = getDateRange(dateRange).endDate;
      }
      dateFilters.dateRange = dateRange;

      setSelectedDateRange(dateRange);
    }

    const queryFilters: PaymentRecordsFilters = {
      orgId: Number(queryParams.get("orgId")) || undefined,
      accountId: Number(queryParams.get("accountId")) || undefined,
      paymentStatus: queryParams.get("paymentStatus") || PaymentStatus.All,
      transactionType:
        queryParams.get("transactionType") || TransactionType.All,
      ...dateFilters,
      page: Number(queryParams.get("page")) || undefined,
      pageSize: Number(queryParams.get("pageSize")) || undefined,
      sortBy: queryParams.get("sortBy") || undefined,
      sortOrder: queryParams.get("sortOrder") || undefined,
    };

    const tempPaymentsFiltersFromQueryStrings = Object.entries(
      queryFilters,
    ).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const tempFilterValuesFromQueryStrings = Object.entries({
      orgId: tempPaymentsFiltersFromQueryStrings.orgId,
      accountId: tempPaymentsFiltersFromQueryStrings.accountId,
      paymentStatus: tempPaymentsFiltersFromQueryStrings.paymentStatus,
      transactionType: tempPaymentsFiltersFromQueryStrings.transactionType,
    }).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    Object.values(tempFilterValuesFromQueryStrings).length > 0 &&
      setFilterValues({
        ...filterValues,
        ...tempFilterValuesFromQueryStrings,
      });
    setPaymentRecordsFilters({
      ...paymentRecordsFilters,
      ...tempPaymentsFiltersFromQueryStrings,
    });
    setPaymentsPaginationModel({
      ...paymentsPaginationModel,
      page: tempPaymentsFiltersFromQueryStrings.page || 0,
      pageSize: tempPaymentsFiltersFromQueryStrings.pageSize || 10,
    });
  }, []);

  useEffect(() => {
    const filters = Object.entries(paymentRecordsFilters).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    setQueryParams({
      ...filters,
    });
  }, [paymentRecordsFilters]);

  useEffect(() => {
    if (creditCardReturnMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Payment refund success!",
        severity: "success",
      });

      handleRefresh();
    }

    if (creditCardReturnMutation.isError) {
      setToastOptions({
        open: true,
        message: "Payment refund failed!",
        severity: "error",
      });
    }
  }, [
    creditCardReturnMutation.isLoading,
    creditCardReturnMutation.isSuccess,
    creditCardReturnMutation.isError,
  ]);

  useEffect(() => {
    if (creditCardReversalMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Payment reversal success!",
        severity: "success",
      });

      handleRefresh();
    }

    if (creditCardReversalMutation.isError) {
      setToastOptions({
        open: true,
        message: "Payment reversal failed!",
        severity: "error",
      });
    }
  }, [
    creditCardReversalMutation.isLoading,
    creditCardReversalMutation.isSuccess,
    creditCardReversalMutation.isError,
  ]);

  return (
    <Box
      sx={{
        marginTop: "50px",
        width: "100%",
        boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "10px",
      }}
    >
      <Toast setOptions={setToastOptions} options={toastOptions} />
      <Box>
        <Grid2
          container
          direction={"column"}
          spacing={2}
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            backgroundColor: "#f5f5f5",
          }}
        >
          <PaymentsToolBar
            onRefresh={handleRefresh}
            onDownload={handleDownload}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            resetPage={resetPage}
            paymentRecordsFilters={paymentRecordsFilters}
            setPaymentRecordsFilters={setPaymentRecordsFilters}
          />
        </Grid2>

        <Box
          sx={{
            display: {
              xs: showMobileTransactionMenu ? "flex" : "none",
              md: "flex",
            },
            justifyContent: {
              xs: "center",
              md: "flex-end",
            },
            px: 2,
            pt: 1,
            pb: 1,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              cursor: "pointer",
            }}
            onClick={() => {
              resetPage();

              setFilterValues((prev) => ({
                ...prev,
                transactionType: TransactionType.All,
              }));

              setPaymentRecordsFilters((prev) => ({
                ...prev,
                transactionType: TransactionType.All,
              }));

              if (showMobileTransactionMenu) {
                setShowMobileTransactionMenu(false);
              }
            }}
          >
            Show All Transactions
          </Typography>
        </Box>

        <Box
          sx={{
            display: {
              xs: showMobileTransactionMenu ? "flex" : "none",
              md: "flex",
            },
            flexDirection: { xs: "column", md: "row" },
            gap: 1,
            px: 2,
            py: 1,
            width: "100%",
          }}
        >
          {PAYMENT_TRANSACTION_TILES.map((tile) => {
            const data =
              tile.key === TransactionType.Sale
                ? paymentRecords?.summaryTotals?.payments
                : paymentRecords?.summaryTotals?.refunds;

            return (
              <Box
                key={tile.key}
                onClick={() => {
                  resetPage();

                  setFilterValues((prev) => ({
                    ...prev,
                    transactionType: tile.key,
                  }));

                  setPaymentRecordsFilters((prev) => ({
                    ...prev,
                    transactionType: tile.key,
                  }));

                  setShowMobileTransactionMenu(false);
                }}
                sx={{
                  cursor: "pointer",
                  flex: 1,
                  minWidth: 220,
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  backgroundColor:
                    paymentRecordsFilters.transactionType === tile.key
                      ? "#d8e5f2bb"
                      : "#fff",
                  border: "1px solid #e5e7eb",
                  borderTop: `3px solid ${tile.color}`,
                  boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "12px",
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {tile.title}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {data?.count ?? 0}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "#6b7280",
                      }}
                    >
                      {tile.key === TransactionType.Sale
                        ? "Transactions"
                        : "Processed"}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: 600,
                        color: tile.color,
                        lineHeight: 1,
                      }}
                    >
                      {formatCurrency(data?.amount ?? 0)}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#6b7280",
                      }}
                    >
                      total
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: {
              xs: showMobileTransactionMenu ? "none" : "contents",
              md: "contents",
            },
          }}
        >
          {!showMobileTransactionMenu && (
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                px: 2,
                py: 1,
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setShowMobileTransactionMenu(true);
                }}
              >
                ← Back
              </Typography>
            </Box>
          )}
          <DataGrid
            columns={columns}
            rows={paymentRecords?.items || []}
            loading={
              fetchPaymentRecordsQuery.isLoading ||
              fetchPaymentRecordsQuery.isRefetching ||
              creditCardReturnMutation.isLoading ||
              creditCardReversalMutation.isLoading
            }
            sortModel={[
              {
                field: paymentRecordsFilters.sortBy || "",
                sort:
                  (paymentRecordsFilters.sortOrder as GridSortDirection) ||
                  null,
              },
            ]}
            onSortModelChange={handleSortModelChange}
            paginationModel={paymentsPaginationModel}
            setPaginationModel={setPaymentsPaginationModel}
            totalRecords={paymentRecords?.totalItems}
            paginationMode="server"
            rowCount={
              paymentRecords?.totalItems ? paymentRecords?.totalItems : 0
            }
            pageSize={paymentRecords?.pageSize}
            sx={{
              border: "none",
              backgroundColor: theme.palette.secondary.light,
              padding: "20px",
              paddingTop: "0px",
              "& .MuiSvgIcon-root": {
                fontSize: 16,
              },
              borderBottomLeftRadius: "10px",
              borderBottomRightRadius: "10px",
              height: "70vh",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
